import { createContext, useContext } from 'react';
import type { GameSnapshot, ProjectedGameState } from '../socket';
import {
  FONT_STACKS,
  THEMES,
  type DigitStyle,
  type FrameStyle,
  type Theme,
} from '../channels/scoreboard/themes';
import type { LayoutTheme } from './types';

// Live data + styling handed to every widget. Widgets read this rather
// than taking props, so a zone template can bind any widget anywhere.
export interface WidgetData {
  snapshot: GameSnapshot | null;
  state: ProjectedGameState | null;
  theme: Theme;
  mode: DigitStyle;
  /** Resolved CSS font-family for layout text. */
  font: string;
  /** Border treatment for zone panels. */
  frame: FrameStyle;
  /** True when rendering sample data (no live game linked). */
  isSample: boolean;
}

// Merge a layout's optional theme override onto the display's base theme +
// digit style, returning the styling fields every render path needs.
export function resolveLayoutTheme(
  base: Theme,
  baseMode: DigitStyle,
  lt: LayoutTheme | undefined,
): { theme: Theme; mode: DigitStyle; font: string; frame: FrameStyle } {
  const presetTheme = lt?.preset ? THEMES[lt.preset] : undefined;
  const start = presetTheme ?? base;
  const theme: Theme = {
    name: start.name,
    bg: lt?.bg ?? start.bg,
    ac: lt?.ac ?? start.ac,
    br: lt?.br ?? start.br,
    lbl: lt?.lbl ?? start.lbl,
    sub: lt?.sub ?? start.sub,
  };
  return {
    theme,
    mode: lt?.digitStyle ?? baseMode,
    font: FONT_STACKS[lt?.font ?? 'default'],
    frame: lt?.frame ?? 'flat',
  };
}

export const WidgetContext = createContext<WidgetData | null>(null);

export function useWidgetData(): WidgetData {
  const ctx = useContext(WidgetContext);
  if (!ctx) throw new Error('useWidgetData must be used inside WidgetContext');
  return ctx;
}

/** Resolve a side's roster (home/away) from the snapshot. */
export function useSide(side: 'home' | 'away') {
  const { snapshot, state } = useWidgetData();
  return {
    roster: snapshot?.rosters[side] ?? null,
    side: state?.[side] ?? null,
  };
}
